import { Link } from "react-router-dom";
import type { CommunityPost } from "@/mocks/posts";
import { formatCount } from "@/utils/format";

interface PostCardProps {
  post: CommunityPost;
  rank?: number;
  variant?: "feature" | "default" | "compact";
}

export default function PostCard({ post, rank, variant = "default" }: PostCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/post/${post.id}`}
        className="group flex gap-4 py-4 border-b border-background-200 last:border-0 cursor-pointer"
      >
        {typeof rank === "number" && (
          <span
            className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-sm font-heading font-bold ${
              rank <= 3
                ? "bg-accent-500 text-background-50"
                : "bg-background-200 text-foreground-600"
            }`}
          >
            {rank}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <h4 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2">
            {post.title}
          </h4>
          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {formatCount(post.heat)}
            </span>
            <span className="hidden sm:inline">·</span>
            <span>{post.category}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">{post.time}</span>
          </span>
        </span>
      </Link>
    );
  }

  const isFeature = variant === "feature";

  return (
    <Link
      to={`/post/${post.id}`}
      className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors cursor-pointer h-full"
    >
      <span className={`${isFeature ? "h-56 md:h-72" : "h-44"} w-full block overflow-hidden relative`}>
        <img
          src={post.cover}
          alt={post.coverAlt}
          className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-md bg-background-50/95 text-foreground-800 text-xs font-medium">
            {post.category}
          </span>
          {post.essence && (
            <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
              加精
            </span>
          )}
          {post.pinned && (
            <span className="px-2.5 py-1 rounded-md bg-secondary-500 text-background-50 text-xs font-medium">
              置顶
            </span>
          )}
        </span>
      </span>
      <span className="flex flex-col flex-1 p-5">
        <h3
          className={`font-heading font-bold text-foreground-950 group-hover:text-primary-700 transition-colors ${
            isFeature ? "text-xl md:text-2xl leading-snug" : "text-base md:text-lg leading-snug"
          }`}
        >
          {post.title}
        </h3>
        <span className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {post.summary}
        </span>
        <span className="mt-auto pt-5 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[11px] font-medium shrink-0">
              {post.author.slice(0, 1)}
            </span>
            <span className="truncate">{post.author}</span>
          </span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {formatCount(post.heat)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {formatCount(post.replyCount)}
            </span>
          </span>
        </span>
      </span>
    </Link>
  );
}